import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, MessageSquare, AlertTriangle, Pencil, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MessagesTab() {
  const [employeeMessages, setEmployeeMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [messageForm, setMessageForm] = useState({ employee_number: "", employee_name: "", message: "" });

  useEffect(() => {
    fetchEmployeeMessages();
  }, []);

  const fetchEmployeeMessages = async () => {
    setLoadingMessages(true);
    try {
      const response = await axios.get(`${API}/admin/employee-messages`);
      setEmployeeMessages(response.data);
    } catch (error) {
      console.error("Failed to load employee messages:", error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleCreateMessage = async () => {
    if (!messageForm.employee_number || !messageForm.message) {
      toast.error("Please enter employee number and message");
      return;
    }
    try {
      await axios.post(`${API}/admin/employee-messages`, messageForm);
      toast.success("Reminder message created successfully");
      setShowMessageDialog(false);
      setMessageForm({ employee_number: "", employee_name: "", message: "" });
      fetchEmployeeMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create message");
    }
  };

  const handleUpdateMessage = async (messageId, updates) => {
    try {
      await axios.put(`${API}/admin/employee-messages/${messageId}`, updates);
      toast.success("Message updated");
      fetchEmployeeMessages();
    } catch (error) {
      toast.error("Failed to update message");
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!confirm("Are you sure you want to delete this message?")) return;
    try {
      await axios.delete(`${API}/admin/employee-messages/${messageId}`);
      toast.success("Message deleted");
      fetchEmployeeMessages();
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const handleToggleMessage = async (message) => {
    await handleUpdateMessage(message.id, { is_active: !message.is_active });
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-vault-text tracking-tight">Employee Messages</h1>
          <p className="text-vault-text-secondary font-manrope mt-1">Create reminder messages that show to employees on their next check-in</p>
        </div>
        <Button onClick={() => { setEditingMessage(null); setMessageForm({ employee_number: "", employee_name: "", message: "" }); setShowMessageDialog(true); }} className="vault-btn-primary" data-testid="add-message-btn">
          <Plus className="w-4 h-4 mr-2" />Add Message
        </Button>
      </div>

      <Card className="bg-amber-500/10 border-amber-500/30 mb-6">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
            <div>
              <p className="text-amber-400 font-semibold">How it works:</p>
              <ul className="text-vault-text-secondary text-sm mt-1 space-y-1">
                <li>- Create a message for an employee by their ID number</li>
                <li>- When they check in next time, a popup will appear with your message</li>
                <li>- They must tap "I UNDERSTAND" before they can continue check-in</li>
                <li>- Message auto-disables after they acknowledge it (one-time reminder)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-vault-surface border-vault-border">
        <CardHeader>
          <CardTitle className="text-vault-text flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-vault-gold" />Active & Past Messages
            <span className="text-vault-text-secondary text-sm font-normal ml-2">({employeeMessages.length} total)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingMessages ? (
            <div className="text-center py-8">
              <div className="w-6 h-6 border-2 border-vault-gold border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-vault-text-secondary mt-2">Loading messages...</p>
            </div>
          ) : employeeMessages.length === 0 ? (
            <div className="text-center py-8 text-vault-text-secondary">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No messages yet</p>
              <p className="text-sm mt-1">Click "Add Message" to create a reminder for an employee</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-vault-border">
                  <TableHead className="text-vault-text-secondary">Status</TableHead>
                  <TableHead className="text-vault-text-secondary">Employee</TableHead>
                  <TableHead className="text-vault-text-secondary">Message</TableHead>
                  <TableHead className="text-vault-text-secondary">Created</TableHead>
                  <TableHead className="text-vault-text-secondary">Acknowledged</TableHead>
                  <TableHead className="text-vault-text-secondary text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employeeMessages.map((msg) => (
                  <TableRow key={msg.id} className="border-vault-border">
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${msg.is_active ? 'bg-amber-500/20 text-amber-400' : 'bg-vault-surface-highlight text-vault-text-secondary'}`}>
                        {msg.is_active ? 'Pending' : 'Done'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div><p className="text-vault-text font-medium">{msg.employee_name || 'Unknown'}</p><p className="text-vault-text-secondary text-sm">#{msg.employee_number}</p></div>
                    </TableCell>
                    <TableCell className="max-w-xs"><p className="text-vault-text truncate">{msg.message}</p></TableCell>
                    <TableCell className="text-vault-text-secondary text-sm">{msg.created_at ? new Date(msg.created_at).toLocaleDateString() : '—'}</TableCell>
                    <TableCell className="text-vault-text-secondary text-sm">{msg.acknowledged_at ? new Date(msg.acknowledged_at).toLocaleDateString() : '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleToggleMessage(msg)}
                          className={msg.is_active ? "border-red-500/50 text-red-400 hover:bg-red-500/10" : "border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"}
                          data-testid={`toggle-message-${msg.id}`}>
                          {msg.is_active ? 'Disable' : 'Enable'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setEditingMessage(msg); setMessageForm({ employee_number: msg.employee_number, employee_name: msg.employee_name || "", message: msg.message }); setShowMessageDialog(true); }}
                          className="border-vault-border text-vault-text hover:bg-vault-surface-highlight" data-testid={`edit-message-${msg.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDeleteMessage(msg.id)}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid={`delete-message-${msg.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="bg-vault-surface border-vault-border">
          <DialogHeader>
            <DialogTitle className="text-vault-text">{editingMessage ? 'Edit Message' : 'Create Reminder Message'}</DialogTitle>
            <DialogDescription className="text-vault-text-secondary">This message will appear when the employee checks in next time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="vault-label">Employee Number *</label>
              <Input value={messageForm.employee_number} onChange={(e) => setMessageForm({...messageForm, employee_number: e.target.value.replace(/[^0-9]/g, '')})} placeholder="Enter employee ID (numbers only)" className="vault-input" disabled={!!editingMessage} data-testid="message-employee-number" />
            </div>
            <div>
              <label className="vault-label">Employee Name (optional)</label>
              <Input value={messageForm.employee_name} onChange={(e) => setMessageForm({...messageForm, employee_name: e.target.value})} placeholder="Will be auto-filled if found in system" className="vault-input" data-testid="message-employee-name" />
            </div>
            <div>
              <label className="vault-label">Message *</label>
              <textarea value={messageForm.message} onChange={(e) => setMessageForm({...messageForm, message: e.target.value})} placeholder="Example: You forgot your room key last time. Please return it to front desk." className="vault-input w-full h-32 resize-none" data-testid="message-text" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageDialog(false)} className="border-vault-border text-vault-text">Cancel</Button>
            <Button onClick={editingMessage ? () => { handleUpdateMessage(editingMessage.id, { message: messageForm.message }); setShowMessageDialog(false); } : handleCreateMessage} className="vault-btn-primary" data-testid="save-message-btn">
              {editingMessage ? 'Update Message' : 'Create Message'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
